import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { KitSolar, DadosInstalacao, DadosIrradiacao, ParametrosFinanceiros, EquipamentoSolar } from "@/types";
import { Plus, Save } from "lucide-react";
import { toast } from "sonner";
import { KitSolarEditor } from "./KitSolarEditor";
import { SystemTypeSelector, SistemaBase } from "./SystemTypeSelector";
import { KitSolarList } from "./KitSolarList";
import { ResultadoFinanceiroDisplay } from "./ResultadoFinanceiroDisplay";
import { ParametrosFinanceirosForm } from "./ParametrosFinanceirosForm";
import { 
  calcularPotenciaIdeal, 
  gerarKitsSimulados, 
  getSistemaPadraoConfig,
  calcularCustoTelhado
} from "./utils/kitSolarCalculations";

interface KitSolarDisplayProps {
  dadosInstalacao: DadosInstalacao;
  dadosIrradiacao: DadosIrradiacao;
  onKitSelecionado: (kit: KitSolar) => void;
  parametrosFinanceiros?: ParametrosFinanceiros | null;
  onParametrosFinanceirosSalvos?: (params: ParametrosFinanceiros) => void;
}

export const KitSolarDisplay = ({
  dadosInstalacao,
  dadosIrradiacao,
  onKitSelecionado,
  parametrosFinanceiros,
  onParametrosFinanceirosSalvos,
}: KitSolarDisplayProps) => {
  const [kits, setKits] = useState<KitSolar[]>([]);
  const [loading, setLoading] = useState(false);
  const [kitSelecionado, setKitSelecionado] = useState<string | null>(null);
  const [kitAtual, setKitAtual] = useState<KitSolar | null>(null);
  const [showEditor, setShowEditor] = useState(false);
  const [kitEmEdicao, setKitEmEdicao] = useState<KitSolar | undefined>(undefined);
  const [isNewKit, setIsNewKit] = useState(false);
  const [tipoSistema, setTipoSistema] = useState<string>("tradicional");
  const [sistemasConfig, setSistemasConfig] = useState<Record<string, SistemaBase>>({});
  const [parametrosFinanceirosState, setParametrosFinanceirosState] = useState<ParametrosFinanceiros | null>(
    parametrosFinanceiros || null
  );
  

  // Carregar configurações do sistema
  useEffect(() => {
    const loadSistemasConfig = () => {
      const tipos = ["tradicional", "mlpe", "hibrido", "offgrid"];
      const configs: Record<string, SistemaBase> = {};
      
      tipos.forEach(tipo => {
        const saved = localStorage.getItem(`sistema_${tipo}`);
        if (saved) {
          configs[tipo] = JSON.parse(saved);
        }
      });
      
      if (Object.keys(configs).length > 0) {
        setSistemasConfig(configs);
      } else {
        setSistemasConfig(getSistemaPadraoConfig());
      }
    };
    
    loadSistemasConfig();
  }, []);

  // Carregar kits quando os dados mudarem
  useEffect(() => {
    if (Object.keys(sistemasConfig).length > 0 && 
        dadosInstalacao.consumoMensal > 0 && 
        dadosIrradiacao.irradiacaoMedia > 0) {
      buscarKits();
    }
  }, [tipoSistema, sistemasConfig, dadosInstalacao.consumoMensal, dadosIrradiacao.irradiacaoMedia, dadosInstalacao.tipoTelhado]);

  // Buscar kits disponíveis
  const buscarKits = async () => {
    if (dadosInstalacao.consumoMensal <= 0 || dadosIrradiacao.irradiacaoMedia <= 0) {
      toast.error("Consumo mensal e irradiação precisam ser valores positivos.");
      return;
    }
    
    setLoading(true);
    
    try {
      // Verificar se existem kits salvos
      const kitsCadastrados = localStorage.getItem('kitsSolares');
      const kitsSalvos = localStorage.getItem('kitsSolaresSalvos');
      
      // Tentar carregar kits salvos primeiro
      if (kitsSalvos) {
        try {
          const kitsSalvosData = JSON.parse(kitsSalvos) as KitSolar[];
          if (kitsSalvosData.length > 0) {
            setKits(kitsSalvosData);
            toast.success("Kits salvos carregados com sucesso!");
            setLoading(false);
            return;
          }
        } catch (error) {
          console.error("Erro ao carregar kits salvos:", error);
        }
      }
      
      // Se não houver kits salvos, tentar os cadastrados
      if (kitsCadastrados) {
        const kitsStorage = JSON.parse(kitsCadastrados) as KitSolar[];
        
        if (kitsStorage.length > 0) {
          // Adicionar custo do telhado aos kits cadastrados
          const kitsComCustoTelhado = kitsStorage.map(kit => {
            const custoTelhado = calcularCustoTelhado(dadosInstalacao.tipoTelhado, kit.potenciaSistema);
            return {
              ...kit,
              custoTelhado,
              precoTotal: kit.precoTotal + custoTelhado
            };
          });
          
          setKits(kitsComCustoTelhado);
          toast.success("Kits cadastrados carregados com sucesso!");
          setLoading(false);
          return;
        }
      }
      
      const configSistema = sistemasConfig[tipoSistema] || sistemasConfig.tradicional;
      
      const apiUrl = `https://api.edeltecsolar.com.br/kits?apiKey=7a6e84ea-65ef-4810-a424-12cb2f722ca4&consumo=${dadosInstalacao.consumoMensal}&irradiacao=${dadosIrradiacao.irradiacaoMedia}`;
      
      try {
        const response = await fetch(apiUrl);
        
        if (!response.ok) {
          throw new Error(`API respondeu com status: ${response.status}`);
        }
        
        const data = await response.json();
        
        const kitsComCustoTelhado = data.map((kit: KitSolar) => {
          const custoTelhado = calcularCustoTelhado(dadosInstalacao.tipoTelhado, kit.potenciaSistema);
          return {
            ...kit,
            custoTelhado,
            precoTotal: kit.precoTotal + custoTelhado
          };
        });
        
        setKits(kitsComCustoTelhado);
        toast.success("Kits solares encontrados com sucesso!");
      } catch (apiError) {
        console.log("Erro ao conectar com API Edeltec, usando dados simulados:", apiError);
        
        const potenciaIdeal = calcularPotenciaIdeal(
          dadosInstalacao.consumoMensal,
          dadosIrradiacao.irradiacaoMedia,
          configSistema.eficiencia,
          configSistema.perdas
        );
        
        const kitsSimulados = gerarKitsSimulados(
          potenciaIdeal, 
          configSistema, 
          tipoSistema, 
          dadosIrradiacao,
          dadosInstalacao
        );
        
        setKits(kitsSimulados);
        toast.success(`Kits solares do tipo ${configSistema.nome} carregados!`);
      }
    } catch (error) {
      console.error("Erro ao buscar kits solares:", error);
      toast.error("Erro ao buscar kits solares. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const handleSelecionarKit = (kit: KitSolar) => {
    setKitSelecionado(kit.id);
    setKitAtual(kit);
    onKitSelecionado(kit);
    toast.info(`Kit ${kit.nome} selecionado.`);
  };

  const handleEditarKit = (kit: KitSolar) => {
    const kitCopy = JSON.parse(JSON.stringify(kit));
    setKitEmEdicao(kitCopy);
    setIsNewKit(false);
    setShowEditor(true);
  };

  const handleNovoKit = () => {
    setKitEmEdicao(undefined);
    setIsNewKit(true);
    setShowEditor(true);
  };

  const handleSalvarKit = (kit: KitSolar) => {
    if (isNewKit) {
      setKits([...kits, kit]);
      
      // Salvar no localStorage para compartilhamento com configuração
      const kitsSalvos = localStorage.getItem('kitsSolaresSalvos');
      let todosKits = kitsSalvos ? JSON.parse(kitsSalvos) : [];
      todosKits.push(kit);
      localStorage.setItem('kitsSolaresSalvos', JSON.stringify(todosKits));
      
      toast.success("Novo kit adicionado com sucesso!");
    } else {
      const kitsAtualizados = kits.map(k => k.id === kit.id ? kit : k);
      setKits(kitsAtualizados);
      
      // Atualizar no localStorage
      const kitsSalvos = localStorage.getItem('kitsSolaresSalvos');
      if (kitsSalvos) {
        let todosKits = JSON.parse(kitsSalvos);
        todosKits = todosKits.map((k: KitSolar) => k.id === kit.id ? kit : k);
        localStorage.setItem('kitsSolaresSalvos', JSON.stringify(todosKits));
      }
      
      if (kitSelecionado === kit.id) {
        setKitAtual(kit);
        onKitSelecionado(kit);
      }
      
      toast.success("Kit atualizado com sucesso!");
    }
    
    // Disparar evento para notificar outras páginas da mudança
    const event = new Event('storage');
    window.dispatchEvent(event);
    
    setShowEditor(false);
  };

  const handleDialogOpenChange = (open: boolean) => {
    if (!open) {
      setShowEditor(false);
    }
  };

  const handleChangeTipoSistema = (value: string) => {
    setTipoSistema(value);
    setKits([]);
    setKitSelecionado(null);
    setKitAtual(null);
    // The useEffect will automatically trigger buscarKits()
  };
  
  const handleParametrosFinanceirosSalvos = (params: ParametrosFinanceiros) => {
    setParametrosFinanceirosState(params);
    if (onParametrosFinanceirosSalvos) {
      onParametrosFinanceirosSalvos(params);
    }
  };

  const salvarComponentesParaReutilizacao = () => {
    if (kitAtual) {
      // Salvar os painéis
      if (kitAtual.paineis && kitAtual.paineis.length > 0) {
        const paineisSalvos = localStorage.getItem('paineisCadastrados');
        let paineis: EquipamentoSolar[] = paineisSalvos ? JSON.parse(paineisSalvos) : [];
        
        kitAtual.paineis.forEach(painel => {
          // Verificar se já existe um painel com marca e modelo idênticos
          const painelExistente = paineis.find(
            p => p.marca === painel.marca && p.modelo === painel.modelo
          );
          
          if (!painelExistente) {
            paineis.push({
              ...painel,
              tipo: 'painel',
              disponibilidade: true
            });
          }
        });
        
        localStorage.setItem('paineisCadastrados', JSON.stringify(paineis));
      }
      
      // Salvar os inversores
      if (kitAtual.inversores && kitAtual.inversores.length > 0) {
        const inversoresSalvos = localStorage.getItem('inversoresCadastrados');
        let inversores: EquipamentoSolar[] = inversoresSalvos ? JSON.parse(inversoresSalvos) : [];
        
        kitAtual.inversores.forEach(inversor => {
          // Verificar se já existe um inversor com marca e modelo idênticos
          const inversorExistente = inversores.find(
            i => i.marca === inversor.marca && i.modelo === inversor.modelo
          );
          
          if (!inversorExistente) {
            inversores.push({
              ...inversor,
              tipo: 'inversor',
              disponibilidade: true
            });
          }
        });
        
        localStorage.setItem('inversoresCadastrados', JSON.stringify(inversores));
      }
      
      // Salvar outros equipamentos
      if (kitAtual.outrosEquipamentos && kitAtual.outrosEquipamentos.length > 0) {
        const outrosEquipamentosSalvos = localStorage.getItem('outrosEquipamentosCadastrados');
        let outrosEquipamentos: EquipamentoSolar[] = outrosEquipamentosSalvos ? JSON.parse(outrosEquipamentosSalvos) : [];
        
        kitAtual.outrosEquipamentos.forEach(outro => {
          // Verificar se já existe um equipamento com marca e modelo idênticos
          const equipamentoExistente = outrosEquipamentos.find(
            e => e.marca === outro.marca && e.modelo === outro.modelo
          );
          
          if (!equipamentoExistente) {
            outrosEquipamentos.push({
              ...outro,
              tipo: 'outros',
              disponibilidade: true
            });
          }
        });
        
        localStorage.setItem('outrosEquipamentosCadastrados', JSON.stringify(outrosEquipamentos));
      }
      
      // Disparar evento para notificar outras páginas da mudança
      const event = new Event('storage');
      window.dispatchEvent(event);
      
      toast.success("Componentes salvos com sucesso para reutilização!");
    } else {
      toast.error("Selecione um kit primeiro");
    }
  };

  // Get equipment data when needed for KitSolarEditor
  const getEquipamentosDisponiveis = () => {
    return {
      paineis: localStorage.getItem('paineisCadastrados') ? JSON.parse(localStorage.getItem('paineisCadastrados') || '[]') : [],
      inversores: localStorage.getItem('inversoresCadastrados') ? JSON.parse(localStorage.getItem('inversoresCadastrados') || '[]') : [],
      outros: localStorage.getItem('outrosEquipamentosCadastrados') ? JSON.parse(localStorage.getItem('outrosEquipamentosCadastrados') || '[]') : []
    };
  };

  return (
    <>
      <SystemTypeSelector 
        tipoSistema={tipoSistema}
        sistemasConfig={sistemasConfig}
        onChangeTipoSistema={handleChangeTipoSistema}
      />

      <Card className="mt-6">
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Kits Solares Recomendados</CardTitle>
              <CardDescription>
                Com base nos dados informados, veja os kits solares recomendados para o sistema {sistemasConfig[tipoSistema]?.nome || "selecionado"}
                {dadosInstalacao.tipoTelhado && (
                  <span className="block mt-1">
                    Tipo de telhado: <strong>{dadosInstalacao.tipoTelhado}</strong> - custo adicional calculado na instalação
                  </span>
                )}
              </CardDescription>
            </div>
            <div className="flex gap-2">
              {kitAtual && (
                <Button 
                  onClick={salvarComponentesParaReutilizacao} 
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <Save className="h-4 w-4" /> Salvar Componentes
                </Button>
              )}
              <Button onClick={handleNovoKit} className="flex items-center gap-2">
                <Plus className="h-4 w-4" /> Criar Kit
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <KitSolarList 
            kits={kits}
            loading={loading}
            kitSelecionado={kitSelecionado}
            dadosInstalacao={dadosInstalacao}
            onSelecionarKit={handleSelecionarKit}
            onEditarKit={handleEditarKit}
            onBuscarKits={buscarKits}
            onNovoKit={handleNovoKit}
          />
        </CardContent>
        {kits.length > 0 && kitSelecionado && (
          <CardFooter className="flex justify-between border-t pt-6">
            <p className="text-sm text-muted-foreground">
              Potência ideal para o consumo:{" "}
              <strong>
                {calcularPotenciaIdeal(
                  dadosInstalacao.consumoMensal,
                  dadosIrradiacao.irradiacaoMedia,
                  sistemasConfig[tipoSistema]?.eficiencia,
                  sistemasConfig[tipoSistema]?.perdas
                ).toFixed(2)}{" "}
                kWp
              </strong>
            </p>
          </CardFooter>
        )}
      </Card>

      {kitAtual && (
        <div className="mt-8">
          <h2 className="text-xl font-semibold mb-4">Parâmetros Financeiros</h2>
          <ParametrosFinanceirosForm 
            onDadosSalvos={handleParametrosFinanceirosSalvos} 
            dadosIniciais={parametrosFinanceirosState || undefined}
            kitSolar={kitAtual}
          />
        </div>
      )}
      
      {kitAtual && parametrosFinanceirosState && (
        <ResultadoFinanceiroDisplay 
          kitSolar={kitAtual}
          parametrosFinanceiros={parametrosFinanceirosState}
          dadosInstalacao={dadosInstalacao}
          dadosIrradiacao={dadosIrradiacao}
        />
      )}

      <Dialog open={showEditor} onOpenChange={handleDialogOpenChange}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {isNewKit ? "Criar Novo Kit Solar" : `Editar Kit: ${kitEmEdicao?.nome}`}
            </DialogTitle>
            <DialogDescription>
              Personalize seu kit solar, adicionando equipamentos e ajustando valores
            </DialogDescription>
          </DialogHeader>
          
          {showEditor && kitEmEdicao !== undefined && (
            <KitSolarEditor 
              kitInicial={kitEmEdicao}
              potenciaAlvo={calcularPotenciaIdeal(
                dadosInstalacao.consumoMensal,
                dadosIrradiacao.irradiacaoMedia,
                sistemasConfig[tipoSistema]?.eficiencia,
                sistemasConfig[tipoSistema]?.perdas
              )}
              onSalvarKit={handleSalvarKit}
            />
          )}
          
          {showEditor && kitEmEdicao === undefined && (
            <KitSolarEditor 
              potenciaAlvo={calcularPotenciaIdeal(
                dadosInstalacao.consumoMensal,
                dadosIrradiacao.irradiacaoMedia,
                sistemasConfig[tipoSistema]?.eficiencia,
                sistemasConfig[tipoSistema]?.perdas
              )}
              onSalvarKit={handleSalvarKit}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
